import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  GraduationCap,
  KeyRound,
  Mail,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  UserSquare2,
} from "lucide-react";

import { useAuth } from "../../../../auth/useAuth";
import ActionToast from "../../../../core/components/ActionToast";
import {
  archiveCampusStaff,
  createCampusStaff,
  deleteCampusStaff,
  loadCampusStaffDashboard,
  setCampusDefaultAccess,
  sendCampusStaffInviteEmail,
  setCampusTeacherPortalAccess,
  uploadCampusStaffPhoto,
  updateCampusStaff,
} from "../../services/staffService";
import CampusInlineLoading from "../../components/CampusInlineLoading";

function SectionCard({ icon: Icon, title, children, action = null }) {
  return (
    <section style={styles.sectionCard}>
      <div style={styles.sectionHeader}>
        <div style={styles.sectionHeaderLeft}>
          <div style={styles.sectionIcon}>
            <Icon size={18} />
          </div>
          <div style={styles.sectionTitle}>{title}</div>
        </div>
        {action}
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </section>
  );
}

function LabeledInput({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        style={styles.input}
      />
    </label>
  );
}

function LabeledTextarea({ label, value, onChange, rows = 4 }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        style={styles.textarea}
      />
    </label>
  );
}

function LabeledTokens({ label, values, onChange, placeholder }) {
  return (
    <LabeledInput
      label={label}
      value={(values || []).join(", ")}
      onChange={(value) =>
        onChange(
          value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        )
      }
      placeholder={placeholder}
    />
  );
}

function SummaryPill({ children, tone = "default" }) {
  const toneStyles =
    tone === "primary"
      ? styles.summaryPillPrimary
      : tone === "warn"
        ? styles.summaryPillWarn
        : styles.summaryPill;

  return <div style={toneStyles}>{children}</div>;
}

function assignmentSourceLabel(source) {
  if (source === "grade-and-direct") return "Grade + Direct";
  if (source === "direct") return "Direct";
  return "Grade";
}

function isBlankStaffDraft(staff) {
  if (!staff) return false;

  const hasAssignments =
    (staff.gradeAssignments || []).length > 0 ||
    (staff.studentAssignments || []).length > 0 ||
    (staff.classroomAssignments || []).length > 0 ||
    (staff.subjectAssignments || []).length > 0 ||
    (staff.programAssignments || []).length > 0 ||
    (staff.tags || []).length > 0;

  return (
    !staff.linkedUserId &&
    !staff.linkedAccount &&
    !String(staff.firstName || "").trim() &&
    !String(staff.lastName || "").trim() &&
    !String(staff.email || "").trim() &&
    !String(staff.phone || "").trim() &&
    !String(staff.jobTitle || "").trim() &&
    !String(staff.biography || "").trim() &&
    !String(staff.notes || "").trim() &&
    !String(staff.accountNotes || "").trim() &&
    !hasAssignments &&
    (!String(staff.displayName || "").trim() ||
      String(staff.displayName || "").trim() === "New Staff Member")
  );
}

export default function StaffPage() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  const [account, setAccount] = useState(null);
  const [inviteCode, setInviteCode] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailOpen, setDetailOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignmentSavingId, setAssignmentSavingId] = useState("");
  const [loading, setLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [studentQuery, setStudentQuery] = useState("");
  const [saveFeedback, setSaveFeedback] = useState("idle");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const staffPhotoInputRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    async function loadDashboard() {
      try {
        setLoading(true);
        setError("");
        const dashboard = await loadCampusStaffDashboard(user?.id);
        if (!mounted) return;
        setStaff(dashboard.staff || []);
        setStudents(dashboard.students || []);
        setAccount(dashboard.account || null);
        setInviteCode(dashboard.inviteCode || "");
        const firstId = dashboard.staff?.[0]?.id || "";
        setSelectedStaffId((current) => current || firstId);
      } catch (loadError) {
        console.error("Campus staff load error:", loadError);
        if (!mounted) return;
        setError(loadError.message || "Could not load staff.");
      } finally {
        if (mounted) {
          setLoading(false);
          setBootstrapped(true);
        }
      }
    }

    loadDashboard();
    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const filteredStaff = useMemo(() => {
    const normalized = String(query || "").trim().toLowerCase();

    return staff.filter((item) => {
      const statusMatches =
        statusFilter === "all" ||
        String(item.employmentStatus || "").toLowerCase() === statusFilter;

      const queryMatches =
        !normalized ||
        [
          item.displayName,
          item.email,
          item.staffType,
          item.jobTitle,
          ...(item.gradeAssignments || []),
          ...(item.classroomAssignments || []),
          ...(item.subjectAssignments || []),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalized));

      return statusMatches && queryMatches;
    });
  }, [query, staff, statusFilter]);

  const selectedStaff =
    filteredStaff.find((item) => item.id === selectedStaffId) ||
    staff.find((item) => item.id === selectedStaffId) ||
    filteredStaff[0] ||
    staff[0] ||
    null;

  useEffect(() => {
    if (!selectedStaff) {
      setEditForm(null);
      return;
    }

    setEditForm({
      ...selectedStaff,
      gradeAssignments: [...(selectedStaff.gradeAssignments || [])],
      studentAssignments: [...(selectedStaff.studentAssignments || [])],
      classroomAssignments: [...(selectedStaff.classroomAssignments || [])],
      subjectAssignments: [...(selectedStaff.subjectAssignments || [])],
      programAssignments: [...(selectedStaff.programAssignments || [])],
      tags: [...(selectedStaff.tags || [])],
    });
    setStudentQuery("");
  }, [selectedStaff?.id]);

  useEffect(() => {
    if (!notice && !error) return undefined;

    const timeoutId = window.setTimeout(() => {
      setNotice("");
      setError("");
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [notice, error]);

  useEffect(() => {
    if (saveFeedback !== "saved") return undefined;

    const timeoutId = window.setTimeout(() => {
      setSaveFeedback("idle");
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [saveFeedback]);

  const previewAssignedStudents = useMemo(() => {
    if (!editForm) return [];

    const gradeAssignments = new Set(
      (editForm.gradeAssignments || []).map((value) => String(value).toLowerCase())
    );
    const directAssignments = new Set(
      (editForm.studentAssignments || []).map((value) => String(value))
    );

    return students
      .filter((student) => {
        const matchesGrade = gradeAssignments.has(
          String(student.gradeLevel || "").toLowerCase()
        );
        const matchesDirect = directAssignments.has(String(student.id || ""));
        return matchesGrade || matchesDirect;
      })
      .map((student) => {
        const matchesGrade = gradeAssignments.has(
          String(student.gradeLevel || "").toLowerCase()
        );
        const matchesDirect = directAssignments.has(String(student.id || ""));
        return {
          ...student,
          assignmentSource: matchesGrade && matchesDirect
            ? "grade-and-direct"
            : matchesGrade
              ? "grade"
              : "direct",
        };
      })
      .sort((left, right) =>
        String(left.displayName || "").localeCompare(String(right.displayName || ""))
      );
  }, [editForm, students]);

  const availableStudentResults = useMemo(() => {
    const normalized = String(studentQuery || "").trim().toLowerCase();
    if (!normalized) {
      return students.slice(0, 8);
    }

    return students
      .filter((student) =>
        [
          student.displayName,
          student.gradeLevel,
          student.studentNumber,
          student.householdName,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalized))
      )
      .slice(0, 8);
  }, [studentQuery, students]);

  const selectedStaffPortalUserId =
    selectedStaff?.linkedUserId || selectedStaff?.linkedAccount?.userId || "";
  const canDeleteSelectedDraft = isBlankStaffDraft(selectedStaff);
  const schoolCode = String(account?.edu_device_code || account?.deviceCode || "").trim();
  const teacherLoginPath = schoolCode
    ? `/campus/teacher/login/${encodeURIComponent(schoolCode)}`
    : "/campus/teacher/login";

  async function toggleDirectStudentAssignment(studentId) {
    if (!user?.id || !editForm?.id) return;

    const existing = new Set((editForm.studentAssignments || []).map((value) => String(value)));
    if (existing.has(String(studentId))) {
      existing.delete(String(studentId));
    } else {
      existing.add(String(studentId));
    }

    const nextAssignments = Array.from(existing);
    const nextForm = {
      ...editForm,
      studentAssignments: nextAssignments,
    };

    setAssignmentSavingId(String(studentId));
    setError("");
    setEditForm(nextForm);

    try {
      const updated = await updateCampusStaff(user.id, nextForm);

      setStaff((current) =>
        current.map((item) =>
          item.id === updated.id
            ? {
                ...item,
                ...updated,
                linkedAccount: item.linkedAccount || null,
                teacherPortalAccess: item.teacherPortalAccess || false,
                assignedStudents: item.assignedStudents || [],
                primaryOwnedGrades: item.primaryOwnedGrades || [],
              }
            : item
        )
      );
      await refreshDashboard(selectedStaffId || updated.id);
      setNotice(
        nextAssignments.includes(String(studentId))
          ? "Student assigned."
          : "Student removed from direct assignments."
      );
      setSaveFeedback("saved");
    } catch (saveError) {
      console.error("Campus direct student assignment save error:", saveError);
      setError(saveError.message || "Could not update direct student assignments.");
      setEditForm(editForm);
    } finally {
      setAssignmentSavingId("");
    }
  }

  async function refreshDashboard(preferredStaffId = selectedStaffId) {
    const dashboard = await loadCampusStaffDashboard(user?.id);
    setStaff(dashboard.staff || []);
    setStudents(dashboard.students || []);
    setAccount(dashboard.account || null);
    setInviteCode(dashboard.inviteCode || "");
    setSelectedStaffId(preferredStaffId || dashboard.staff?.[0]?.id || "");
  }

  async function handleAddStaff() {
    if (!user?.id) return;

    try {
      setSaving(true);
      setError("");
      const created = await createCampusStaff(user.id);
      await refreshDashboard(created.id);
      setDetailOpen(true);
      setNotice("Staff record created.");
    } catch (createError) {
      console.error("Campus staff create error:", createError);
      setError(createError.message || "Could not create staff record.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    if (!user?.id || !editForm?.id) return;

    try {
      setSaving(true);
      setSaveFeedback("saving");
      setError("");
      await updateCampusStaff(user.id, editForm);
      await refreshDashboard(editForm.id);
      setDetailOpen(true);
      setNotice("Staff record saved.");
      setSaveFeedback("saved");
    } catch (saveError) {
      console.error("Campus staff save error:", saveError);
      setError(saveError.message || "Could not save staff record.");
      setSaveFeedback("idle");
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    if (!user?.id || !selectedStaff?.id) return;

    try {
      setSaving(true);
      setError("");
      await archiveCampusStaff(user.id, selectedStaff.id);
      await refreshDashboard("");
      setDetailOpen(false);
      setNotice("Staff record archived.");
    } catch (archiveError) {
      console.error("Campus staff archive error:", archiveError);
      setError(archiveError.message || "Could not archive staff record.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteDraft() {
    if (!user?.id || !selectedStaff?.id || !canDeleteSelectedDraft) return;

    try {
      setSaving(true);
      setError("");
      await deleteCampusStaff(user.id, selectedStaff.id);
      await refreshDashboard("");
      setDetailOpen(false);
      setNotice("Blank staff draft deleted.");
    } catch (deleteError) {
      console.error("Campus staff delete draft error:", deleteError);
      setError(deleteError.message || "Could not delete the blank staff draft.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteStaff() {
    if (!user?.id || !selectedStaff?.id) return;

    const staffName = selectedStaff.displayName || "this staff member";
    if (!window.confirm(`Delete ${staffName}? This permanently removes the campus staff record.`)) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      await deleteCampusStaff(user.id, selectedStaff.id);
      await refreshDashboard("");
      setDetailOpen(false);
      setNotice("Staff record deleted.");
    } catch (deleteError) {
      console.error("Campus staff delete error:", deleteError);
      setError(deleteError.message || "Could not delete the staff record.");
    } finally {
      setSaving(false);
    }
  }

  async function handleInviteEmail() {
    if (!selectedStaff) return;

    try {
      const result = await sendCampusStaffInviteEmail({
        accountId: account?.id || "",
        email: selectedStaff.email,
        recipientName: selectedStaff.displayName,
        inviteCode,
        staffId: selectedStaff.id,
      });
      setNotice(result?.message || `Invite email sent to ${selectedStaff.email}.`);
    } catch (inviteError) {
      console.error("Campus staff invite email error:", inviteError);
      setError(inviteError.message || "Could not send the invite email.");
    }
  }

  async function handleTeacherPortalAccess(enabled) {
    if (!user?.id || !account?.id || !selectedStaffPortalUserId) {
      setError("This staff member needs a linked account before you can control teacher portal access.");
      return;
    }

    try {
      setAccessSaving(true);
      setError("");
      await setCampusTeacherPortalAccess({
        userId: user.id,
        accountId: account.id,
        targetUserId: selectedStaffPortalUserId,
        enabled,
      });
      await refreshDashboard(selectedStaff.id);
      setNotice(
        enabled
          ? "Teacher portal access enabled."
          : "Teacher portal access removed."
      );
    } catch (accessError) {
      console.error("Campus teacher portal access error:", accessError);
      setError(accessError.message || "Could not update teacher portal access.");
    } finally {
      setAccessSaving(false);
    }
  }

  async function handleCampusAccess(enabled) {
    if (!user?.id || !account?.id || !selectedStaffPortalUserId) {
      setError("This staff member needs a linked account before you can control campus access.");
      return;
    }

    try {
      setAccessSaving(true);
      setError("");
      await setCampusDefaultAccess({
        userId: user.id,
        accountId: account.id,
        targetUserId: selectedStaffPortalUserId,
        enabled,
      });
      await refreshDashboard(selectedStaff.id);
      setNotice(enabled ? "Campus access enabled." : "Campus access removed.");
    } catch (accessError) {
      console.error("Campus default access error:", accessError);
      setError(accessError.message || "Could not update campus access.");
    } finally {
      setAccessSaving(false);
    }
  }

  async function handleStaffPhotoSelected(event) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || !user?.id || !selectedStaff?.id) {
      return;
    }

    try {
      setPhotoUploading(true);
      setError("");
      const nextStaff = await uploadCampusStaffPhoto(user.id, selectedStaff.id, file);

      setStaff((current) =>
        current.map((item) => (item.id === nextStaff.id ? { ...item, ...nextStaff } : item))
      );
      setEditForm((current) =>
        current && current.id === nextStaff.id
          ? { ...current, photoPath: nextStaff.photoPath, photoUrl: nextStaff.photoUrl }
          : current
      );
      setNotice("Staff photo updated.");
    } catch (uploadError) {
      console.error("Campus staff photo upload error:", uploadError);
      setError(uploadError.message || "Could not upload staff photo.");
    } finally {
      setPhotoUploading(false);
    }
  }

  const activeTeachers = staff.filter(
    (item) =>
      item.isActive && String(item.staffType || "").toLowerCase() === "teacher"
  ).length;

  if (loading && !bootstrapped) {
    return (
      <CampusInlineLoading
        title="Loading Staff"
        detail="Preparing staff records, teaching assignments, and access settings..."
      />
    );
  }

  if (!account) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyTitle}>No campus organization found</div>
        <div style={styles.emptyText}>
          Create or join a campus organization in Settings before managing staff.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div>
          <div style={styles.eyebrow}>Staff</div>
          <h1 style={styles.title}>Manage teachers, assignments, and account access</h1>
          <p style={styles.subtitle}>
            Keep staff records together with grade ownership, classroom and subject assignments,
            account linkage, onboarding, and teacher-to-student visibility.
          </p>
        </div>
        <div style={styles.heroActions}>
          <button type="button" style={styles.primaryButton} onClick={handleAddStaff} disabled={saving}>
            <Plus size={16} />
            Add Staff
          </button>
        </div>
      </div>

      <ActionToast message={error} tone="error" onDismiss={() => setError("")} />
      <ActionToast message={!error ? notice : ""} tone="success" onDismiss={() => setNotice("")} />

      <div style={styles.metricGrid}>
        <MetricCard label="Staff" value={String(staff.filter((item) => item.isActive).length)} detail="Active campus staff" />
        <MetricCard label="Teachers" value={String(activeTeachers)} detail="Teacher records" />
        <MetricCard label="Linked" value={String(staff.filter((item) => item.linkedAccount).length)} detail="Account-linked staff" />
        <MetricCard label="Students" value={String(students.length)} detail="Students available to assign" />
      </div>

      {!detailOpen ? (
        <div style={styles.rosterShell}>
          <aside style={styles.rosterSidebar}>
            <div style={styles.sidebarActionRow}>
              <button type="button" style={styles.sidebarAddButton} onClick={handleAddStaff} disabled={saving}>
                <Plus size={16} />
                {saving ? "Adding Staff..." : "Add Staff"}
              </button>
            </div>

            <label style={styles.searchBar}>
              <Search size={18} color="var(--color-primary)" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search name, login, grade, classroom, subject, or role"
                style={styles.searchInput}
              />
            </label>

            <div style={styles.filterRow}>
              {[
                ["all", "All Statuses"],
                ["active", "Active"],
                ["pending", "Pending"],
                ["inactive", "Inactive"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  style={statusFilter === value ? styles.filterButtonActive : styles.filterButton}
                  onClick={() => setStatusFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div style={styles.resultsSummary}>
              {filteredStaff.length} staff member{filteredStaff.length === 1 ? "" : "s"} found
            </div>

            <div style={styles.rosterList}>
              {filteredStaff.length === 0 ? (
                <div style={styles.emptyCard}>
                  <div style={styles.emptyTitle}>No staff found</div>
                  <div style={styles.emptyText}>Add staff here, or try a different search term.</div>
                  <button type="button" style={styles.emptyActionButton} onClick={handleAddStaff} disabled={saving}>
                    <Plus size={16} />
                    {saving ? "Adding Staff..." : "Add Staff"}
                  </button>
                </div>
              ) : (
                filteredStaff.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedStaffId(item.id)}
                    style={selectedStaff?.id === item.id ? styles.rosterRowActive : styles.rosterRow}
                  >
                    <img src={item.photoUrl} alt={item.displayName} style={styles.rosterThumb} />
                    <div style={styles.rosterBody}>
                      <div style={styles.rosterName}>{item.displayName || "Unnamed Staff"}</div>
                      <div style={styles.rosterMeta}>
                        {item.staffType || "Staff"} • {item.employmentStatus || "Status not set"}
                      </div>
                      <div style={styles.rosterMeta}>
                        {item.portalUsername || item.email || "No login username"}
                      </div>
                      <div style={styles.rosterChips}>
                        <span style={styles.cardPill}>
                          {(item.gradeAssignments || []).join(", ") || "No grades assigned"}
                        </span>
                        {item.eduTeacherId ? <span style={styles.portalBadgeActive}>Login ready</span> : null}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </aside>

          <section style={styles.selectionPanel}>
            {selectedStaff ? (
              <div style={styles.previewPanel}>
                <div style={styles.previewHero}>
                  <img src={selectedStaff.photoUrl} alt={selectedStaff.displayName} style={styles.previewPhoto} />
                  <div style={styles.previewHeroBody}>
                    <div style={styles.previewKicker}>{selectedStaff.staffType || "Staff Record"}</div>
                    <div style={styles.previewName}>{selectedStaff.displayName || "Unnamed Staff"}</div>
                    <div style={styles.previewMeta}>
                      {selectedStaff.jobTitle || "No job title"} • {selectedStaff.email || "No email"}
                    </div>
                  </div>
                </div>

                <div style={styles.previewStats}>
                  <div style={styles.previewStatCard}>
                    <div style={styles.previewStatLabel}>Employment</div>
                    <div style={styles.previewStatValue}>{selectedStaff.employmentStatus || "Not set"}</div>
                  </div>
                  <div style={styles.previewStatCard}>
                    <div style={styles.previewStatLabel}>Staff Login</div>
                    <div style={styles.previewStatValue}>
                      {selectedStaff.portalUsername || "Not set"}
                    </div>
                  </div>
                  <div style={styles.previewStatCard}>
                    <div style={styles.previewStatLabel}>Teacher Portal</div>
                    <div style={styles.previewStatValue}>
                      {selectedStaff.eduTeacherId ? "Ready" : "Not configured"}
                    </div>
                  </div>
                  <div style={styles.previewStatCard}>
                    <div style={styles.previewStatLabel}>Students</div>
                    <div style={styles.previewStatValue}>
                      {(selectedStaff.assignedStudents || []).length || 0}
                    </div>
                  </div>
                </div>

                <div style={styles.previewSection}>
                  <div style={styles.previewSectionTitle}>Assignments</div>
                  <div style={styles.previewTagRow}>
                    {(selectedStaff.gradeAssignments || []).length ? (
                      selectedStaff.gradeAssignments.map((grade) => (
                        <span key={grade} style={styles.previewTag}>Grade {grade}</span>
                      ))
                    ) : (
                      <span style={styles.previewTagMuted}>No grade assignments</span>
                    )}
                  </div>
                  <div style={styles.previewSectionText}>
                    {(selectedStaff.subjectAssignments || []).join(", ") || "No subject assignments"}
                  </div>
                  <div style={styles.previewSectionText}>
                    {(selectedStaff.classroomAssignments || []).join(", ") || "No classroom assignments"}
                  </div>
                </div>

                <div style={styles.previewSection}>
                  <div style={styles.previewSectionTitle}>Login Information</div>
                  <div style={styles.previewSectionText}>School code: {schoolCode || inviteCode || "Not set"}</div>
                  <div style={styles.previewSectionText}>Username: {selectedStaff.portalUsername || "Not set"}</div>
                  <div style={styles.previewSectionText}>
                    Staff login is stored in the database-backed EDU teacher table.
                  </div>
                </div>

                <div style={styles.previewActions}>
                  <button type="button" style={styles.editButton} onClick={() => setDetailOpen(true)}>
                    <Save size={16} />
                    Manage Staff Record
                  </button>
                  <a style={styles.secondaryLinkButton} href={teacherLoginPath}>
                    <KeyRound size={16} />
                    Open Teacher Login
                  </a>
                </div>
              </div>
            ) : (
              <div style={styles.emptyCard}>
                <div style={styles.emptyTitle}>No staff selected</div>
                <div style={styles.emptyText}>Choose a staff member from the roster or create a new one.</div>
              </div>
            )}
          </section>
        </div>
      ) : (
        <main style={styles.mainColumnFull}>
          {selectedStaff && editForm ? (
            <>
              <section style={styles.profileHero}>
                <div style={styles.profileAvatarColumn}>
                  <img
                    src={editForm.photoUrl || selectedStaff.photoUrl}
                    alt={selectedStaff.displayName}
                    style={styles.profileAvatar}
                  />
                  <input
                    ref={staffPhotoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleStaffPhotoSelected}
                    style={styles.hiddenFileInput}
                  />
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => staffPhotoInputRef.current?.click()}
                    disabled={photoUploading}
                  >
                    {photoUploading ? "Uploading Photo..." : "Upload Photo"}
                  </button>
                </div>
                <div style={styles.profileBody}>
                  <div style={styles.profileName}>{selectedStaff.displayName}</div>
                  <div style={styles.profileMeta}>
                    {selectedStaff.jobTitle || selectedStaff.staffType || "Staff"} • {selectedStaff.email || "No email"}
                  </div>
                  <div style={styles.profileMetaMuted}>
                    {selectedStaff.primaryOwnedGrades?.length
                      ? `Primary teacher for ${selectedStaff.primaryOwnedGrades.join(", ")}`
                      : "Shared or non-grade assignment"}
                  </div>
                </div>
                <div style={styles.profileActions}>
                  <button type="button" style={styles.secondaryButton} onClick={() => setDetailOpen(false)} disabled={saving}>
                    <ArrowLeft size={15} />
                    Back To Staff
                  </button>
                  {canDeleteSelectedDraft ? (
                    <button type="button" style={styles.dangerButton} onClick={handleDeleteDraft} disabled={saving}>
                      <Trash2 size={15} />
                      Delete Draft
                    </button>
                  ) : (
                    <button type="button" style={styles.dangerButton} onClick={handleDeleteStaff} disabled={saving}>
                      <Trash2 size={15} />
                      Delete Staff
                    </button>
                  )}
                  <button type="button" style={styles.primaryButton} onClick={handleSave} disabled={saving}>
                    <Save size={15} />
                    {saving
                      ? "Saving..."
                      : saveFeedback === "saved"
                        ? "Saved"
                        : "Save Staff"}
                  </button>
                </div>
              </section>

              <div style={styles.sectionGrid}>
                <SectionCard
                  icon={KeyRound}
                  title="Staff Login"
                  action={
                    <button type="button" style={styles.compactPrimaryButton} onClick={handleSave} disabled={saving}>
                      <Save size={14} />
                      {saving ? "Saving..." : "Save Login"}
                    </button>
                  }
                >
                  <div style={styles.summaryPillRow}>
                    <SummaryPill tone={editForm.eduTeacherId ? "primary" : "default"}>
                      {editForm.eduTeacherId ? "Database login ready" : "No login saved"}
                    </SummaryPill>
                    <SummaryPill>School code: {schoolCode || "Not set"}</SummaryPill>
                    <SummaryPill>{teacherLoginPath}</SummaryPill>
                  </div>
                  <div style={styles.fieldGrid}>
                    <LabeledInput
                      label="Username"
                      value={editForm.portalUsername || ""}
                      onChange={(value) => setEditForm((current) => ({ ...current, portalUsername: value }))}
                      placeholder="Used with the school code"
                    />
                    <LabeledInput
                      label="Password"
                      type="password"
                      value={editForm.portalPassword || ""}
                      onChange={(value) => setEditForm((current) => ({ ...current, portalPassword: value }))}
                      placeholder={editForm.eduTeacherId ? "Leave blank to keep current password" : "At least 8 characters"}
                    />
                  </div>
                  <div style={styles.fieldHint}>
                    Staff use the school code plus this username and password. This saves to the shared EDU teacher login table, not Supabase Auth.
                  </div>
                </SectionCard>

                <SectionCard icon={UserSquare2} title="Staff Profile">
                  <div style={styles.fieldGrid}>
                    <LabeledInput label="First Name" value={editForm.firstName} onChange={(value) => setEditForm((current) => ({ ...current, firstName: value, displayName: `${value} ${current.lastName || ""}`.trim() }))} />
                    <LabeledInput label="Last Name" value={editForm.lastName} onChange={(value) => setEditForm((current) => ({ ...current, lastName: value, displayName: `${current.firstName || ""} ${value}`.trim() }))} />
                    <LabeledInput label="Display Name" value={editForm.displayName} onChange={(value) => setEditForm((current) => ({ ...current, displayName: value }))} />
                    <LabeledInput label="Email" type="email" value={editForm.email} onChange={(value) => setEditForm((current) => ({ ...current, email: value }))} />
                    <LabeledInput label="Phone" value={editForm.phone} onChange={(value) => setEditForm((current) => ({ ...current, phone: value }))} />
                    <LabeledInput label="Alternate Phone" value={editForm.alternatePhone} onChange={(value) => setEditForm((current) => ({ ...current, alternatePhone: value }))} />
                    <LabeledInput label="Staff Type" value={editForm.staffType} onChange={(value) => setEditForm((current) => ({ ...current, staffType: value }))} />
                    <LabeledInput label="Job Title" value={editForm.jobTitle} onChange={(value) => setEditForm((current) => ({ ...current, jobTitle: value }))} />
                    <LabeledInput label="Staff Number" value={editForm.staffNumber} onChange={(value) => setEditForm((current) => ({ ...current, staffNumber: value }))} />
                    <LabeledInput label="Employment Status" value={editForm.employmentStatus} onChange={(value) => setEditForm((current) => ({ ...current, employmentStatus: value }))} />
                    <LabeledInput label="Hire Date" type="date" value={editForm.hireDate} onChange={(value) => setEditForm((current) => ({ ...current, hireDate: value }))} />
                    <LabeledInput label="Start Date" type="date" value={editForm.startDate} onChange={(value) => setEditForm((current) => ({ ...current, startDate: value }))} />
                  </div>
                  <div style={styles.fieldHint}>Use the upload button beside the staff photo to choose a profile image.</div>
                  <LabeledTextarea label="Biography" value={editForm.biography} onChange={(value) => setEditForm((current) => ({ ...current, biography: value }))} />
                </SectionCard>

                <SectionCard icon={Building2} title="Assignments">
                  <div style={styles.summaryPillRow}>
                    <SummaryPill tone="primary">
                      {(editForm.gradeAssignments || []).length || 0} grade assignments
                    </SummaryPill>
                    <SummaryPill>
                      {(editForm.classroomAssignments || []).length || 0} classrooms
                    </SummaryPill>
                    <SummaryPill>
                      {(editForm.subjectAssignments || []).length || 0} subjects
                    </SummaryPill>
                    <SummaryPill tone="warn">
                      {(editForm.studentAssignments || []).length || 0} direct students
                    </SummaryPill>
                  </div>
                  <div style={styles.fieldGrid}>
                    <LabeledTokens label="Grades" values={editForm.gradeAssignments} onChange={(values) => setEditForm((current) => ({ ...current, gradeAssignments: values }))} placeholder="K, 1, 2" />
                    <LabeledTokens label="Classrooms" values={editForm.classroomAssignments} onChange={(values) => setEditForm((current) => ({ ...current, classroomAssignments: values }))} placeholder="Room 201, Bluebirds" />
                    <LabeledTokens label="Subjects" values={editForm.subjectAssignments} onChange={(values) => setEditForm((current) => ({ ...current, subjectAssignments: values }))} placeholder="Music, Art, Math" />
                    <LabeledTokens label="Programs" values={editForm.programAssignments} onChange={(values) => setEditForm((current) => ({ ...current, programAssignments: values }))} placeholder="Aftercare, Chapel" />
                    <LabeledTokens label="Tags" values={editForm.tags} onChange={(values) => setEditForm((current) => ({ ...current, tags: values }))} placeholder="Lead Teacher, Specials" />
                  </div>
                  <div style={styles.compactSection}>
                    <div style={styles.compactHeader}>
                      <div style={styles.compactTitle}>Direct Student Assignments</div>
                      <div style={styles.compactText}>
                        Add specific students here. Grade assignments still auto-pull whole grade rosters.
                      </div>
                    </div>
                    <div style={styles.searchWrap}>
                      <Search size={16} />
                      <input
                        value={studentQuery}
                        onChange={(event) => setStudentQuery(event.target.value)}
                        placeholder="Search students to assign directly"
                        style={styles.searchInput}
                      />
                    </div>
                    <div style={styles.assignableStudentList}>
                      {availableStudentResults.map((student) => {
                        const isAssigned = (editForm.studentAssignments || []).includes(student.id);
                        return (
                          <div key={student.id} style={styles.assignableStudentCard}>
                            <div>
                              <div style={styles.studentName}>{student.displayName}</div>
                              <div style={styles.studentMeta}>
                                Grade {student.gradeLevel || "Unknown"} • {student.householdName || "No household listed"}
                              </div>
                            </div>
                            <button
                              type="button"
                              style={isAssigned ? styles.secondaryButton : styles.primaryButton}
                              disabled={assignmentSavingId === student.id}
                              onClick={() => toggleDirectStudentAssignment(student.id)}
                            >
                              {assignmentSavingId === student.id
                                ? "Saving..."
                                : isAssigned
                                  ? "Remove"
                                  : "Assign"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </SectionCard>

                <SectionCard icon={ShieldCheck} title="Optional App Account Access">
                  <div style={styles.summaryPillRow}>
                    <SummaryPill tone="primary">
                      School code: {inviteCode || schoolCode || "Unavailable"}
                    </SummaryPill>
                    <SummaryPill>
                      {selectedStaff.linkedAccount
                        ? `Linked ${selectedStaff.linkedAccount.status || "account"}`
                        : "Not linked yet"}
                    </SummaryPill>
                    <SummaryPill>
                      {selectedStaff.email ? "Email on file" : "Missing email"}
                    </SummaryPill>
                    <SummaryPill tone={selectedStaff.teacherPortalAccess ? "primary" : "default"}>
                      {selectedStaff.teacherPortalAccess
                        ? "Teacher portal enabled"
                        : "Teacher portal off"}
                    </SummaryPill>
                  </div>
                  <div style={styles.accountPanelCompact}>
                    <div style={styles.accountStat}>
                      <div style={styles.accountStatLabel}>School Code</div>
                      <div style={styles.accountStatValue}>{inviteCode || schoolCode || "Unavailable"}</div>
                    </div>
                    <div style={styles.accountStat}>
                      <div style={styles.accountStatLabel}>Linked Account</div>
                      <div style={styles.accountStatValue}>
                        {selectedStaff.linkedAccount
                          ? `${selectedStaff.linkedAccount.fullName || selectedStaff.linkedAccount.email} • ${selectedStaff.linkedAccount.status}`
                          : "Not linked yet"}
                      </div>
                    </div>
                    <div style={styles.accountStat}>
                      <div style={styles.accountStatLabel}>Regular Campus Access</div>
                      <div style={styles.accountStatValue}>
                        {selectedStaffPortalUserId
                          ? selectedStaff.campusAccess
                            ? "Enabled"
                            : "Disabled"
                          : "Needs linked account"}
                      </div>
                    </div>
                    <div style={styles.accountStat}>
                      <div style={styles.accountStatLabel}>Teacher Portal Access</div>
                      <div style={styles.accountStatValue}>
                        {selectedStaffPortalUserId
                          ? selectedStaff.teacherPortalAccess
                            ? "Enabled"
                            : "Disabled"
                          : "Needs linked account"}
                      </div>
                    </div>
                  </div>
                  <div style={styles.portalAccessCard}>
                    <div>
                      <div style={styles.portalAccessTitle}>Regular Campus Access</div>
                      <div style={styles.portalAccessText}>
                        Use this for staff who should enter the main Campus experience and standard staff workflows, even if they are not teachers.
                      </div>
                    </div>
                    <button
                      type="button"
                      style={
                        selectedStaff.campusAccess
                          ? styles.portalAccessDisableButton
                          : styles.portalAccessEnableButton
                      }
                      onClick={() => handleCampusAccess(!selectedStaff.campusAccess)}
                      disabled={accessSaving || !selectedStaffPortalUserId}
                    >
                      {accessSaving
                        ? "Saving Access..."
                        : selectedStaff.campusAccess
                          ? "Remove Campus Access"
                          : "Give Campus Access"}
                    </button>
                  </div>
                  <div style={styles.portalAccessCard}>
                    <div>
                      <div style={styles.portalAccessTitle}>Teacher Portal Access</div>
                      <div style={styles.portalAccessText}>
                        Give this staff member access to the teacher-facing campus experience with a fixed set of tiles and no tile store.
                      </div>
                    </div>
                    <button
                      type="button"
                      style={
                        selectedStaff.teacherPortalAccess
                          ? styles.portalAccessDisableButton
                          : styles.portalAccessEnableButton
                      }
                      onClick={() =>
                        handleTeacherPortalAccess(!selectedStaff.teacherPortalAccess)
                      }
                      disabled={accessSaving || !selectedStaffPortalUserId}
                    >
                      {accessSaving
                        ? "Saving Access..."
                        : selectedStaff.teacherPortalAccess
                          ? "Remove Teacher Portal"
                          : "Give Teacher Portal"}
                    </button>
                  </div>
                  {!selectedStaffPortalUserId ? (
                    <div style={styles.accountHint}>
                      This turns on after the staff member has a linked campus account. Invite them first, then choose whether they should get regular Campus access, teacher portal access, or both.
                    </div>
                  ) : null}
                  {canDeleteSelectedDraft ? (
                    <div style={styles.accountHint}>
                      This is still just a blank staff draft, so you can delete it completely if it was created by mistake.
                    </div>
                  ) : null}
                  <div style={styles.accountActions}>
                    <button type="button" style={styles.secondaryButton} onClick={handleInviteEmail}>
                      <Mail size={15} />
                      Open Invite Email
                    </button>
                    {canDeleteSelectedDraft ? (
                      <button type="button" style={styles.dangerButton} onClick={handleDeleteDraft} disabled={saving}>
                        <Trash2 size={15} />
                        Delete Blank Draft
                      </button>
                    ) : (
                      <button type="button" style={styles.dangerButton} onClick={handleDeleteStaff} disabled={saving}>
                        <Trash2 size={15} />
                        Delete Staff
                      </button>
                    )}
                    <button type="button" style={styles.dangerButton} onClick={handleArchive}>
                      <Trash2 size={15} />
                      Archive Staff
                    </button>
                  </div>
                  <LabeledTextarea label="Account Notes" value={editForm.accountNotes} onChange={(value) => setEditForm((current) => ({ ...current, accountNotes: value }))} rows={2} />
                </SectionCard>

                <SectionCard icon={GraduationCap} title="Teacher Students">
                  <div style={styles.summaryPillRow}>
                    <SummaryPill tone="primary">
                      {previewAssignedStudents.length} assigned students
                    </SummaryPill>
                    {selectedStaff.primaryOwnedGrades?.length ? (
                      <SummaryPill>
                        Primary teacher: {selectedStaff.primaryOwnedGrades.join(", ")}
                      </SummaryPill>
                    ) : null}
                  </div>
                  {previewAssignedStudents.length ? (
                    <div style={styles.studentList}>
                      {previewAssignedStudents.map((student) => (
                        <div key={student.id} style={styles.studentCard}>
                          <div style={styles.studentCardHeader}>
                            <div>
                              <div style={styles.studentName}>{student.displayName}</div>
                              <div style={styles.studentMeta}>
                                Grade {student.gradeLevel || "Unknown"} • {student.currentEnrollmentStatus || student.enrollmentStatus || "Not set"}
                              </div>
                            </div>
                            <SummaryPill>{assignmentSourceLabel(student.assignmentSource)}</SummaryPill>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.emptySmall}>
                      No students are visible for this staff member yet. Assign a grade to pull the grade roster here.
                    </div>
                  )}
                </SectionCard>

                <SectionCard icon={Briefcase} title="Internal Notes">
                  <LabeledTextarea label="Staff Notes" value={editForm.notes} onChange={(value) => setEditForm((current) => ({ ...current, notes: value }))} rows={5} />
                </SectionCard>
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyTitle}>No staff selected</div>
              <div style={styles.emptyText}>Choose a staff member from the list or create a new one.</div>
            </div>
          )}
        </main>
      )}
    </div>
  );
}

function MetricCard({ label, value, detail }) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{value}</div>
      <div style={styles.metricDetail}>{detail}</div>
    </div>
  );
}

const styles = {
  page: { color: "#0f172a", display: "flex", flexDirection: "column", gap: 16, padding: 16 },
  loading: { color: "#475569", padding: 24 },
  hero: {
    alignItems: "flex-start",
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 12,
    boxShadow: "0 12px 24px rgba(15,23,42,0.05)",
    display: "flex",
    gap: 20,
    justifyContent: "space-between",
    padding: 18,
  },
  eyebrow: { color: "var(--color-primary)", fontSize: 11, fontWeight: 900, letterSpacing: 0, textTransform: "uppercase" },
  title: { fontSize: 24, lineHeight: 1.15, margin: "6px 0 6px" },
  subtitle: { color: "#475569", fontSize: 14, lineHeight: 1.5, margin: 0, maxWidth: 820 },
  heroActions: { display: "flex", gap: 10 },
  primaryButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: 0,
    borderRadius: 12,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 800,
    gap: 8,
    padding: "14px 18px",
  },
  compactPrimaryButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: 0,
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    gap: 7,
    padding: "9px 12px",
  },
  secondaryButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 12,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 800,
    gap: 8,
    padding: "12px 16px",
  },
  dangerButton: {
    alignItems: "center",
    background: "rgba(254,226,226,0.95)",
    border: "1px solid rgba(248,113,113,0.25)",
    borderRadius: 12,
    color: "#991b1b",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 800,
    gap: 8,
    padding: "12px 16px",
  },
  errorBanner: {
    background: "rgba(254,226,226,0.95)",
    border: "1px solid rgba(248,113,113,0.25)",
    borderRadius: 18,
    color: "#991b1b",
    padding: "12px 14px",
  },
  noticeBanner: {
    background: "rgba(236,253,245,0.95)",
    border: "1px solid rgba(74,222,128,0.24)",
    borderRadius: 18,
    color: "#166534",
    padding: "12px 14px",
  },
  metricGrid: { display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" },
  metricCard: {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 12,
    boxShadow: "0 12px 24px rgba(15,23,42,0.05)",
    padding: 16,
  },
  metricLabel: { color: "#64748b", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" },
  metricValue: { fontSize: 24, fontWeight: 900, marginTop: 8 },
  metricDetail: { color: "#475569", fontSize: 13, marginTop: 8 },
  rosterShell: {
    alignItems: "stretch",
    background: "#ffffff",
    border: "1px solid #dbe5e1",
    borderRadius: 8,
    boxShadow: "0 12px 24px rgba(15,23,42,0.05)",
    display: "grid",
    gap: 0,
    gridTemplateColumns: "390px minmax(0, 1fr)",
    minHeight: 680,
    overflow: "hidden",
  },
  rosterSidebar: {
    borderRight: "1px solid #dbe5e1",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    minHeight: 680,
    padding: 14,
  },
  sidebarActionRow: {
    display: "flex",
    justifyContent: "flex-start",
  },
  sidebarAddButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    padding: "12px 16px",
  },
  searchBar: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #cfe0db",
    borderRadius: 8,
    display: "flex",
    gap: 12,
    padding: "12px 14px",
  },
  resultsSummary: {
    color: "var(--color-primary)",
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  rosterList: {
    display: "grid",
    gap: 8,
    overflow: "auto",
    paddingRight: 2,
  },
  rosterRow: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "56px minmax(0, 1fr)",
    padding: 8,
    textAlign: "left",
  },
  rosterRowActive: {
    alignItems: "center",
    background: "color-mix(in srgb, var(--color-primary) 7%, #ffffff 93%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 45%, #d8e4e0 55%)",
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "56px minmax(0, 1fr)",
    padding: 8,
    textAlign: "left",
  },
  rosterThumb: {
    aspectRatio: "1 / 1",
    borderRadius: 8,
    objectFit: "cover",
    width: "100%",
  },
  rosterBody: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },
  rosterName: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 800,
  },
  rosterMeta: {
    color: "#475569",
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rosterChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 2,
  },
  cardPill: {
    background: "#f1f5f9",
    border: "1px solid #dbe5e1",
    borderRadius: 8,
    color: "#334155",
    fontSize: 11,
    fontWeight: 800,
    padding: "4px 7px",
  },
  portalBadgeActive: {
    background: "#dcfce7",
    border: "1px solid #86efac",
    borderRadius: 8,
    color: "#166534",
    fontSize: 11,
    fontWeight: 900,
    padding: "4px 7px",
  },
  selectionPanel: {
    background: "#f8fafc",
    padding: 16,
  },
  previewPanel: {
    background: "#ffffff",
    border: "1px solid #dbe5e1",
    borderRadius: 8,
    display: "grid",
    gap: 16,
    padding: 18,
  },
  previewHero: {
    alignItems: "center",
    display: "grid",
    gap: 16,
    gridTemplateColumns: "96px minmax(0, 1fr)",
  },
  previewPhoto: {
    aspectRatio: "1 / 1",
    borderRadius: 8,
    objectFit: "cover",
    width: "100%",
  },
  previewHeroBody: { minWidth: 0 },
  previewKicker: {
    color: "var(--color-primary)",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  previewName: {
    color: "#0f172a",
    fontSize: 30,
    fontWeight: 900,
    lineHeight: 1.1,
    marginTop: 4,
  },
  previewMeta: {
    color: "#475569",
    fontSize: 14,
    marginTop: 8,
  },
  previewStats: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  },
  previewStatCard: {
    background: "#f8fafc",
    border: "1px solid #dbe5e1",
    borderRadius: 8,
    padding: 12,
  },
  previewStatLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  previewStatValue: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 900,
    marginTop: 7,
  },
  previewSection: {
    borderTop: "1px solid #e2e8f0",
    display: "grid",
    gap: 8,
    paddingTop: 14,
  },
  previewSectionTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 900,
  },
  previewSectionText: {
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5,
  },
  previewTagRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  previewTag: {
    background: "color-mix(in srgb, var(--color-primary) 10%, #ffffff 90%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 30%, #dbe5e1 70%)",
    borderRadius: 8,
    color: "var(--color-primary)",
    fontSize: 12,
    fontWeight: 900,
    padding: "6px 9px",
  },
  previewTagMuted: {
    background: "#f1f5f9",
    border: "1px solid #dbe5e1",
    borderRadius: 8,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    padding: "6px 9px",
  },
  previewActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  editButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    padding: "12px 16px",
  },
  secondaryLinkButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d6e1dd",
    borderRadius: 8,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    padding: "12px 16px",
    textDecoration: "none",
  },
  emptyCard: {
    background: "#ffffff",
    border: "1px solid #dbe5e1",
    borderRadius: 8,
    padding: 18,
  },
  emptyActionButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    marginTop: 14,
    padding: "12px 16px",
  },
  layout: { display: "grid", gap: 20, gridTemplateColumns: "340px minmax(0, 1fr)" },
  sidebar: {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 12,
    boxShadow: "0 12px 24px rgba(15,23,42,0.05)",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 18,
  },
  searchWrap: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: 14,
    color: "#64748b",
    display: "flex",
    gap: 10,
    padding: "0 12px",
  },
  searchInput: { background: "transparent", border: 0, color: "#0f172a", flex: 1, fontSize: 14, outline: "none", padding: "12px 0" },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  filterButton: {
    background: "#f8fafc",
    border: "1px solid #d6e1dd",
    borderRadius: 8,
    color: "#475569",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 12px",
  },
  filterButtonActive: {
    background: "var(--color-primary)",
    border: "1px solid var(--color-primary)",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 12px",
  },
  select: {
    background: "#ffffff",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 14,
    color: "#0f172a",
    fontSize: 14,
    padding: "12px 14px",
  },
  staffList: { display: "grid", gap: 10, maxHeight: "70vh", overflow: "auto", paddingRight: 4 },
  staffCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: 12,
    cursor: "pointer",
    display: "flex",
    gap: 12,
    padding: 12,
    textAlign: "left",
  },
  staffCardActive: {
    border: "1px solid var(--color-primary)",
    boxShadow: "0 0 0 3px rgba(15,23,42,0.04)",
  },
  staffAvatar: { borderRadius: 16, height: 54, objectFit: "cover", width: 54 },
  staffCardContent: { minWidth: 0 },
  staffCardName: { fontSize: 15, fontWeight: 900 },
  staffCardMeta: { color: "#334155", fontSize: 12, marginTop: 4 },
  staffCardMetaMuted: { color: "#64748b", fontSize: 12, marginTop: 4 },
  mainColumn: { display: "flex", flexDirection: "column", gap: 20 },
  mainColumnFull: { display: "flex", flexDirection: "column", gap: 20, minWidth: 0, width: "100%" },
  profileHero: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 12,
    boxShadow: "0 12px 24px rgba(15,23,42,0.05)",
    display: "flex",
    gap: 18,
    padding: 22,
  },
  profileAvatarColumn: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  profileAvatar: { borderRadius: 22, height: 92, objectFit: "cover", width: 92 },
  hiddenFileInput: { display: "none" },
  profileBody: { flex: 1, minWidth: 0 },
  profileName: { fontSize: 30, fontWeight: 900, lineHeight: 1.05 },
  profileMeta: { color: "#334155", marginTop: 8 },
  profileMetaMuted: { color: "#64748b", fontSize: 13, marginTop: 8 },
  profileActions: { display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end" },
  sectionGrid: { display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" },
  sectionCard: {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 12,
    boxShadow: "0 12px 24px rgba(15,23,42,0.05)",
    padding: 18,
  },
  sectionHeader: { alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 14 },
  sectionHeaderLeft: { alignItems: "center", display: "flex", gap: 10 },
  sectionIcon: {
    alignItems: "center",
    background: "color-mix(in srgb, var(--color-primary) 12%, #ffffff 88%)",
    borderRadius: 12,
    color: "var(--color-primary)",
    display: "flex",
    height: 36,
    justifyContent: "center",
    width: 36,
  },
  sectionTitle: { fontSize: 18, fontWeight: 900 },
  sectionBody: { display: "grid", gap: 14 },
  fieldGrid: { display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" },
  summaryPillRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  summaryPill: {
    background: "rgba(241,245,249,0.95)",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: 999,
    color: "#334155",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 12px",
  },
  summaryPillPrimary: {
    background: "color-mix(in srgb, var(--color-primary) 14%, white)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 28%, white)",
    borderRadius: 999,
    color: "var(--color-primary)",
    fontSize: 12,
    fontWeight: 900,
    padding: "8px 12px",
  },
  summaryPillWarn: {
    background: "rgba(255,247,237,0.95)",
    border: "1px solid rgba(251,146,60,0.24)",
    borderRadius: 999,
    color: "#9a3412",
    fontSize: 12,
    fontWeight: 900,
    padding: "8px 12px",
  },
  compactSection: {
    background: "color-mix(in srgb, var(--color-primary) 6%, #ffffff 94%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 16%, #d8e4e0 84%)",
    borderRadius: 18,
    display: "grid",
    gap: 12,
    padding: 14,
  },
  compactHeader: { display: "grid", gap: 4 },
  compactTitle: { fontSize: 14, fontWeight: 900 },
  compactText: { color: "#64748b", fontSize: 13, lineHeight: 1.5 },
  assignableStudentList: { display: "grid", gap: 10 },
  assignableStudentCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid rgba(148,163,184,0.16)",
    borderRadius: 16,
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    padding: 12,
  },
  fieldHint: { color: "#64748b", fontSize: 13, lineHeight: 1.6 },
  field: { display: "grid", gap: 8 },
  fieldLabel: { color: "#334155", fontSize: 13, fontWeight: 800 },
  input: {
    background: "#ffffff",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 14,
    color: "#0f172a",
    fontSize: 14,
    padding: "12px 14px",
  },
  textarea: {
    background: "#ffffff",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 14,
    color: "#0f172a",
    fontSize: 14,
    minHeight: 90,
    padding: "12px 14px",
    resize: "vertical",
  },
  accountPanel: { display: "grid", gap: 12 },
  accountPanelCompact: { display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" },
  accountStat: {
    background: "color-mix(in srgb, var(--color-primary) 6%, #ffffff 94%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 16%, #d8e4e0 84%)",
    borderRadius: 16,
    padding: 14,
  },
  accountStatLabel: { color: "#64748b", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" },
  accountStatValue: { fontSize: 14, fontWeight: 800, marginTop: 6 },
  portalAccessCard: {
    alignItems: "center",
    background: "color-mix(in srgb, var(--color-primary) 7%, #ffffff 93%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 18%, #d8e4e0 82%)",
    borderRadius: 18,
    display: "flex",
    flexWrap: "wrap",
    gap: 14,
    justifyContent: "space-between",
    padding: 14,
  },
  portalAccessTitle: { fontSize: 14, fontWeight: 900 },
  portalAccessText: { color: "#64748b", fontSize: 13, lineHeight: 1.55, marginTop: 4, maxWidth: 540 },
  portalAccessEnableButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 999,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    justifyContent: "center",
    minWidth: 186,
    padding: "12px 16px",
  },
  portalAccessDisableButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid color-mix(in srgb, var(--color-primary) 24%, #d8e4e0 76%)",
    borderRadius: 999,
    color: "var(--color-primary)",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    justifyContent: "center",
    minWidth: 186,
    padding: "12px 16px",
  },
  accountHint: { color: "#64748b", fontSize: 13, lineHeight: 1.6 },
  accountActions: { display: "flex", flexWrap: "wrap", gap: 10 },
  studentList: { display: "grid", gap: 10, maxHeight: 320, overflow: "auto" },
  studentCard: {
    background: "color-mix(in srgb, var(--color-primary) 6%, #ffffff 94%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 16%, #d8e4e0 84%)",
    borderRadius: 16,
    padding: 12,
  },
  studentCardHeader: { alignItems: "flex-start", display: "flex", gap: 12, justifyContent: "space-between" },
  studentName: { fontSize: 14, fontWeight: 900 },
  studentMeta: { color: "#64748b", fontSize: 12, marginTop: 4 },
  emptyState: {
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: 24,
    padding: 28,
  },
  emptyTitle: { fontSize: 24, fontWeight: 900 },
  emptyText: { color: "#475569", marginTop: 8 },
  emptySmall: { color: "#64748b", lineHeight: 1.6 },
};
